import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useFrameRenderer } from "./frame-renderer-context";
import { useInlineFrameEmbed } from "./inline-frame-embed-context";

interface FrameSurface {
  className: string;
  attributes: Partial<ReturnType<typeof surfaceRoleAttributes>>;
}

const EMBEDDED_FRAME_SURFACE: FrameSurface = { className: "", attributes: {} };
const SOLID_FRAME_SURFACE: FrameSurface = {
  className: "denker-solid-frame",
  attributes: {},
};

/**
 * The surface of a frame root for frame `id`: the glass frame material plus
 * the surface-role and native-glass grouping attributes, or, where the
 * renderer draws solid paper/ink windows, the solid material with no native
 * glass plate.
 *
 * A frame that is always glass (`glass`: a conversation, like the summoned
 * chat) keeps the glass material on solid surfaces too.
 *
 * An embedded copy (inside a conversation reply) draws no surface: no glass
 * background, outline, radius, or native glass plate. The embed card is its
 * only box.
 */
export function useFrameSurface(
  id: string,
  options: { nativeGroup?: string; glass?: boolean } = {},
): FrameSurface {
  const embedded = useInlineFrameEmbed();
  const { material } = useFrameRenderer();
  if (embedded) return EMBEDDED_FRAME_SURFACE;
  if (material === "solid" && !options.glass) return SOLID_FRAME_SURFACE;
  return {
    className: surfaceRoleClassName("frame"),
    attributes: surfaceRoleAttributes("frame", {
      nativeLevel: "root",
      nativeGroup: options.nativeGroup ?? `frame:${id}`,
    }),
  };
}
